import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import './rowGroup.css';

/**
 * A grouped list: an inset card of rows, each one icon → label → value →
 * chevron.
 *
 * This is the component the interface brief called Gap 03. The Hub had tables
 * and it had forms, and every settings-shaped screen was built as a table —
 * which is why they read as data to be scanned rather than as choices to be
 * made. A table answers "what are all the values?"; a grouped list answers
 * "what can I change, and what is it set to?" Those are different questions
 * and Settings only ever asks the second.
 *
 * ── What it refuses ───────────────────────────────────────────────────────
 *
 * No `variant`, no `tone` beyond danger, no per-row padding, no way to make one
 * row taller. Same discipline as SectionRow, and for the same reason: the whole
 * value is that every row in the Hub sits on one rhythm, and the moment a
 * caller can opt out of it, the next caller does too.
 *
 * The chevron is NOT a prop. A row shows one when it leads somewhere — that is,
 * when it has an onClick and no control of its own — because a chevron is a
 * promise about what pressing does. Made optional, it becomes decoration, and
 * then it appears on rows that go nowhere.
 *
 * ── Why a row with an action is a <button> ────────────────────────────────
 *
 * The whole row is the target, including its padding, so the element that IS
 * the row has to be the element that takes the press. A <div onClick> would
 * leave it unreachable by keyboard and unannounced to a screen reader — and
 * this is the pattern the Hub is about to use everywhere, so getting it wrong
 * once gets it wrong in fifty places.
 *
 * A row with a `control` stays a plain <div>: the control inside it is the
 * thing you press, and a button wrapping a button is a lie about the target.
 */

export interface RowGroupProps {
  /** Optional heading above the card. Short — it names the group. */
  title?: string;
  /** One line under the card: the caveat, the "why", the thing that surprises. */
  note?: ReactNode;
  children: ReactNode;
}

export function RowGroup({ title, note, children }: RowGroupProps) {
  return (
    <section>
      <div className="ui-row-group">
        {title === undefined ? null : <h3 className="ui-row-group-title">{title}</h3>}
        {children}
      </div>
      {note === undefined ? null : <p className="ui-row-group-note">{note}</p>}
    </section>
  );
}

export interface RowProps {
  /** The leading icon. Left off, the row's divider lines up with the label. */
  icon?: IconName;
  label: ReactNode;
  /** One quiet line under the label, for the thing the label cannot say. */
  note?: ReactNode;
  /** What it is set to. Right-aligned, one line, ellipsised. */
  value?: ReactNode;
  /**
   * A control that lives in the row — a switch, a select, a small button.
   * Mutually exclusive with a chevron: a row either leads somewhere or is
   * changed in place, never both.
   */
  control?: ReactNode;
  onClick?: (() => void) | undefined;
  /** Destructive. Colours the label and icon; never the row. */
  danger?: boolean;
}

export function Row({ icon, label, note, value, control, onClick, danger = false }: RowProps) {
  const classes = ['ui-row'];
  if (icon === undefined) classes.push('ui-row--plain');
  if (onClick !== undefined && control === undefined) classes.push('ui-row--action');
  if (danger) classes.push('ui-row--danger');

  const inside = (
    <>
      {icon === undefined ? null : (
        <span className="ui-row-icon">
          <Icon name={icon} size="lg" />
        </span>
      )}
      <span className="ui-row-text">
        <span className="ui-row-label">{label}</span>
        {note === undefined ? null : <span className="ui-row-note">{note}</span>}
      </span>
      {value === undefined ? null : <span className="ui-row-value">{value}</span>}
      {control === undefined ? null : <span className="ui-row-control">{control}</span>}
      {onClick !== undefined && control === undefined ? (
        <span className="ui-row-chevron">
          <Icon name="chevron" size="md" />
        </span>
      ) : null}
    </>
  );

  if (onClick !== undefined && control === undefined) {
    return (
      <button type="button" className={classes.join(' ')} onClick={onClick}>
        {inside}
      </button>
    );
  }
  return <div className={classes.join(' ')}>{inside}</div>;
}
